import { useCallback, useMemo, useState } from 'react';

import { createNexusApi } from '../../../../domain/nexus/api';
import type {
  NexusBugEditor,
  NexusBugInput,
  NexusBugList,
  NexusBugReportResult,
} from '../../../../domain/nexus/model';
import { firebaseNexusGateway } from '../../../../platform/firebase/nexusGateway';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import {
  type AuxiliaryCostForm,
  type CostForm,
  type EstimateForm,
  type RaterCostForm,
  confirmCostMessage,
  confirmEstimateMessage,
  emptyAuxiliaryCost,
  emptyCostForm,
  emptyEstimateForm,
  emptyRaterCost,
  parseCostForm,
  parseEstimateForm,
} from './bugReportHelpers';
import { bugInputErrors } from './deliveredTaskHelpers';
import { diagnosticMessage } from './helpers';

const nexusApi = createNexusApi(firebaseNexusGateway);

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return diagnosticMessage(error);
}

const EMPTY_INPUT: NexusBugInput = { projectName: '', projectOwner: '', description: '' };

export function useBugListViewModel() {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<NexusBugList | null>(null);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setData(await nexusApi.fetchNexusBugs());
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.bugs.initialize' });
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, data, initialize, clearError: useCallback(() => setError(null), []) };
}

/** Loads one bug's details into a form and saves them. */
export function useBugEditorViewModel(bugId: string) {
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [editor, setEditor] = useState<NexusBugEditor | null>(null);
  const [saved, setSaved] = useState<NexusBugInput>(EMPTY_INPUT);
  const [form, setForm] = useState<NexusBugInput>(EMPTY_INPUT);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await nexusApi.fetchNexusBug(bugId);
      const values = { projectName: next.bug.projectName, projectOwner: next.bug.projectOwner, description: next.bug.description };
      setEditor(next);
      setSaved(values);
      setForm(values);
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.bugEditor.initialize', metadata: { bugId } });
    } finally {
      setBusy(false);
    }
  }, [bugId]);

  const errors = useMemo(() => bugInputErrors(form), [form]);
  const dirty = (Object.keys(form) as (keyof NexusBugInput)[]).some((field) => form[field].trim() !== saved[field].trim());

  const setField = useCallback((field: keyof NexusBugInput, value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setSavedAt(null);
  }, []);

  const save = useCallback(async () => {
    if (Object.keys(errors).length) return;
    setSaving(true);
    setError(null);
    try {
      const result = await nexusApi.saveNexusBug(bugId, form);
      const values = { projectName: result.projectName, projectOwner: result.projectOwner, description: result.description };
      setSaved(values);
      setForm(values);
      setEditor((previous) => (previous ? { ...previous, bug: result } : previous));
      setSavedAt(result.updatedAt);
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.bugEditor.save', metadata: { bugId } });
    } finally {
      setSaving(false);
    }
  }, [bugId, errors, form]);

  const reset = useCallback(() => setForm(saved), [saved]);

  /** Shows a report just sent to Google as the bug's last report of its kind. */
  const applyReport = useCallback((result: NexusBugReportResult) => {
    const field = result.kind === 'cost' ? 'lastCostReport' : 'lastEstimateReport';
    setEditor((previous) => (previous ? { ...previous, bug: { ...previous.bug, [field]: result.report } } : previous));
  }, []);

  return {
    busy, saving, error, editor, form, errors, dirty, savedAt,
    initialize, setField, save, reset, applyReport, clearError: useCallback(() => setError(null), []),
  };
}

/**
 * Shared send flow for the two report forms: errors show after the first send
 * attempt, the admin confirms before anything goes to Google, and the form
 * clears once Google accepts the report.
 */
function useReportForm<Form, Input, Errors>({
  bugId, kind, empty, parse, confirmMessage, sendReport, onSent,
}: {
  bugId: string;
  kind: 'cost' | 'estimate';
  empty: () => Form;
  parse: (form: Form) => { input: Input | null; errors: Errors };
  confirmMessage: (bugId: string, input: Input) => string;
  sendReport: (bugId: string, input: Input) => Promise<NexusBugReportResult>;
  onSent: (result: NexusBugReportResult) => void;
}) {
  const [form, setForm] = useState<Form>(empty);
  const [attempted, setAttempted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const parsed = useMemo(() => parse(form), [form, parse]);

  const update = useCallback((change: (previous: Form) => Form) => {
    setForm(change);
    setSent(false);
  }, []);

  const send = useCallback(async (confirm: (message: string) => boolean) => {
    setAttempted(true);
    const { input } = parsed;
    if (!input || !confirm(confirmMessage(bugId, input))) return;
    setSending(true);
    setError(null);
    try {
      onSent(await sendReport(bugId, input));
      setForm(empty());
      setAttempted(false);
      setSent(true);
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.bugReport.send', metadata: { bugId, kind } });
    } finally {
      setSending(false);
    }
  }, [bugId, confirmMessage, empty, kind, onSent, parsed, sendReport]);

  return {
    form,
    errors: attempted ? parsed.errors : null,
    sending,
    error,
    sent,
    update,
    send,
    clearError: useCallback(() => setError(null), []),
  };
}

/** The study's estimate (reportEstimates): expected total cost and optional handling time. */
export function useBugEstimateViewModel(bugId: string, onSent: (result: NexusBugReportResult) => void) {
  const report = useReportForm({
    bugId,
    kind: 'estimate',
    empty: emptyEstimateForm,
    parse: parseEstimateForm,
    confirmMessage: confirmEstimateMessage,
    sendReport: nexusApi.reportNexusBugEstimate,
    onSent,
  });
  const { update } = report;
  const setField = useCallback((field: keyof EstimateForm, value: string) => {
    update((previous) => ({ ...previous, [field]: value }));
  }, [update]);
  return { ...report, errors: report.errors ?? {}, setField };
}

/** One period's actual cost (reportMetrics), with rater and auxiliary cost lines. */
export function useBugCostViewModel(bugId: string, onSent: (result: NexusBugReportResult) => void) {
  const report = useReportForm({
    bugId,
    kind: 'cost',
    empty: emptyCostForm,
    parse: parseCostForm,
    confirmMessage: confirmCostMessage,
    sendReport: nexusApi.reportNexusBugCost,
    onSent,
  });
  const { update } = report;

  const setField = useCallback((field: 'reportTime' | 'costUsd' | 'discountedUsd', value: string) => {
    update((previous) => ({ ...previous, [field]: value }));
  }, [update]);

  const setRaterField = useCallback((index: number, field: Exclude<keyof RaterCostForm, 'key'>, value: string) => {
    update((previous) => ({
      ...previous,
      raterCosts: previous.raterCosts.map((line, at) => (at === index ? { ...line, [field]: value } : line)),
    }));
  }, [update]);

  const setAuxiliaryField = useCallback((index: number, field: Exclude<keyof AuxiliaryCostForm, 'key'>, value: string) => {
    update((previous) => ({
      ...previous,
      auxiliaryCosts: previous.auxiliaryCosts.map((line, at) => (at === index ? { ...line, [field]: value } : line)),
    }));
  }, [update]);

  const addRaterCost = useCallback(() => {
    update((previous) => ({ ...previous, raterCosts: [...previous.raterCosts, emptyRaterCost()] }));
  }, [update]);
  const addAuxiliaryCost = useCallback(() => {
    update((previous) => ({ ...previous, auxiliaryCosts: [...previous.auxiliaryCosts, emptyAuxiliaryCost()] }));
  }, [update]);
  const removeLine = useCallback((list: 'raterCosts' | 'auxiliaryCosts', index: number) => {
    update((previous: CostForm) => ({ ...previous, [list]: previous[list].filter((_, at) => at !== index) }));
  }, [update]);

  return {
    ...report,
    errors: report.errors ?? { raterCosts: [], auxiliaryCosts: [] },
    setField, setRaterField, setAuxiliaryField, addRaterCost, addAuxiliaryCost, removeLine,
  };
}
